import { Helmet } from "react-helmet-async";

import { siteConfig } from "@/config/site";

type SeoProps = {
  title: string;
  description?: string;
  canonicalPath?: string;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  imageAlt?: string;
  type?: "website" | "article";
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  rssPath?: string;
  noIndex?: boolean;
  /** Avec `noIndex`, émet `noindex,follow` au lieu de `noindex,nofollow`. */
  allowFollow?: boolean;
  isHomePage?: boolean;
};

const getSiteUrl = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }

  return import.meta.env.VITE_SITE_URL ?? siteConfig.url ?? "";
};

const normalizeUrl = (base: string, path?: string) => {
  if (!path) return base;
  if (!base) return path;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
};

/**
 * Longueur maximale d'une méta description avant coupure.
 *
 * Un tiers des pages du site dépassaient 160 caractères, certaines approchant
 * 380 — le générateur d'articles ne borne pas sa sortie. Le moteur tronque de
 * toute façon ; autant couper proprement sur un mot plutôt que de laisser la
 * phrase s'interrompre au milieu.
 */
const MAX_DESCRIPTION_LENGTH = 155;

export const clampDescription = (value: string) => {
  const normalized = value.replace(/\s+/g, " ").trim();

  if (normalized.length <= MAX_DESCRIPTION_LENGTH) return normalized;

  const sliced = normalized.slice(0, MAX_DESCRIPTION_LENGTH - 1);
  const lastSpace = sliced.lastIndexOf(" ");
  const cut = lastSpace > MAX_DESCRIPTION_LENGTH / 2 ? lastSpace : sliced.length;

  return `${sliced.slice(0, cut).replace(/[\s,;:.!?-]+$/, "")}…`;
};

export default function Seo({
  title,
  description,
  canonicalPath,
  image,
  imageWidth,
  imageHeight,
  imageAlt,
  type = "website",
  jsonLd,
  rssPath = "/rss.xml",
  noIndex,
  allowFollow = false,
  isHomePage = false,
}: SeoProps) {
  const siteUrl = getSiteUrl();
  const canonicalUrl = canonicalPath
    ? normalizeUrl(siteUrl, canonicalPath)
    : undefined;
  const resolvedImage = image ?? siteConfig.ogImage;
  const imageUrl = resolvedImage
    ? normalizeUrl(siteUrl, resolvedImage)
    : undefined;
  const resolvedImageWidth =
    imageWidth ?? (image ? undefined : siteConfig.ogImageWidth);
  const resolvedImageHeight =
    imageHeight ?? (image ? undefined : siteConfig.ogImageHeight);
  const resolvedImageAlt = imageAlt ?? siteConfig.name;
  const rssUrl = rssPath ? normalizeUrl(siteUrl, rssPath) : undefined;
  const siteName = siteConfig.name;
  const siteHomeUrl = normalizeUrl(siteUrl, "/");
  const descriptionValue = clampDescription(
    description?.trim() || siteConfig.description,
  );
  const titleValue = title.includes(siteName)
    ? title
    : `${title} | ${siteName}`;

  const websiteJsonLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    url: siteHomeUrl,
    name: siteName,
    inLanguage: "fr-FR",
    potentialAction: {
      "@type": "SearchAction",
      target: `${siteHomeUrl}tuteurs?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  if (
    Array.isArray(siteConfig.alternateNames) &&
    siteConfig.alternateNames.length
  ) {
    websiteJsonLd.alternateName = siteConfig.alternateNames;
  }

  const baseSchemas: Array<Record<string, unknown>> = isHomePage
    ? [websiteJsonLd]
    : [];

  const combinedJsonLd = jsonLd
    ? Array.isArray(jsonLd)
      ? [...baseSchemas, ...jsonLd]
      : [...baseSchemas, jsonLd]
    : baseSchemas.length === 1
      ? baseSchemas[0]
      : baseSchemas.length > 1
        ? baseSchemas
        : undefined;

  const jsonLdPayload = combinedJsonLd
    ? JSON.stringify(combinedJsonLd, null, 2)
    : null;

  return (
    <Helmet>
      <title>{titleValue}</title>
      <meta content={descriptionValue} name="description" />
      {canonicalUrl && <link href={canonicalUrl} rel="canonical" />}
      {rssUrl && (
        <link href={rssUrl} rel="alternate" type="application/rss+xml" />
      )}
      {/* `follow` est conservé quand la page reste utile au maillage : une page
          ville sans annonce ne doit pas être classée, mais ses liens internes
          doivent continuer à être suivis. Mettre `nofollow` sur des dizaines de
          milliers de pages couperait le graphe de liens du site. */}
      {noIndex ? (
        <meta
          content={allowFollow ? "noindex,follow" : "noindex,nofollow"}
          name="robots"
        />
      ) : (
        <meta content="index,follow" name="robots" />
      )}

      <meta content={titleValue} property="og:title" />
      <meta content={descriptionValue} property="og:description" />
      <meta content={siteName} property="og:site_name" />
      <meta content="fr_FR" property="og:locale" />
      {canonicalUrl && <meta content={canonicalUrl} property="og:url" />}
      <meta content={type} property="og:type" />
      {imageUrl && <meta content={imageUrl} property="og:image" />}
      {imageUrl && resolvedImageWidth != null && (
        <meta content={String(resolvedImageWidth)} property="og:image:width" />
      )}
      {imageUrl && resolvedImageHeight != null && (
        <meta
          content={String(resolvedImageHeight)}
          property="og:image:height"
        />
      )}
      {imageUrl && <meta content={resolvedImageAlt} property="og:image:alt" />}

      <meta
        content={imageUrl ? "summary_large_image" : "summary"}
        name="twitter:card"
      />
      {siteConfig.twitterHandle && (
        <meta content={siteConfig.twitterHandle} name="twitter:site" />
      )}
      <meta content={titleValue} name="twitter:title" />
      <meta content={descriptionValue} name="twitter:description" />
      {imageUrl && <meta content={imageUrl} name="twitter:image" />}

      {jsonLdPayload && (
        <script type="application/ld+json">{jsonLdPayload}</script>
      )}
    </Helmet>
  );
}
